import api from "./api";

export function formatReportDateTime(date) {
  if (!date) return "";
  const dateObj = typeof date === "string" ? new Date(date) : date;
  if (Number.isNaN(dateObj.getTime())) return "";

  const isoString = new Date(
    dateObj.getTime() - dateObj.getTimezoneOffset() * 60000,
  ).toISOString();
  return isoString.replace("T", " ").slice(0, 19);
}

export function formatReportDateShort(date) {
  if (!date) return "";
  const dateObj = typeof date === "string" ? new Date(date) : date;
  if (Number.isNaN(dateObj.getTime())) return "";
  return dateObj.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function formatDateInputValue(date) {
  const dateObj = typeof date === "string" ? new Date(date) : date;
  if (Number.isNaN(dateObj.getTime())) return "";
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const day = String(dateObj.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getPdfReportEndDate() {
  const end = new Date();
  end.setDate(end.getDate() - 1);
  end.setHours(0, 0, 0, 0);
  return end;
}

/** Default PDF dialog range: last 7 calendar days ending yesterday. */
export function getDefaultPdfReportDates() {
  return getPdfReportQuickDates("7d");
}

/** Quick presets for PDF dialog - all end yesterday (full days). */
export function getPdfReportQuickDates(preset) {
  const end = getPdfReportEndDate();
  const start = new Date(end);

  if (preset === "30d") {
    start.setDate(end.getDate() - 29);
  } else if (preset === "3m") {
    start.setMonth(end.getMonth() - 3);
  } else {
    // 7d default - 7 inclusive calendar days
    start.setDate(end.getDate() - 6);
  }

  return {
    startDate: formatDateInputValue(start),
    endDate: formatDateInputValue(end),
    preset: preset,
  };
}

/** Build API date range from date-only inputs (start 00:00:00, end 23:59:59). */
export function buildReportDateRangeFromDates(startDate, endDate) {
  if (!startDate || !endDate) return null;

  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T23:59:59`);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return null;
  }
  if (start > end) return null;

  const todayEnd = new Date();
  todayEnd.setHours(23, 59, 59, 999);
  if (end > todayEnd) return null;

  return { start, end };
}

function getPathDepth(hierarchyPath) {
  return (hierarchyPath || "").split("/").filter(Boolean).length;
}

function parseEnergyValue(value) {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = parseFloat(value.replace(/[^\d.-]/g, ""));
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

export function getRootNodes(hierarchyData) {
  const nodes = Object.values(hierarchyData || {});
  if (!nodes.length) return [];

  const minPathLength = Math.min(
    ...nodes.map((node) => getPathDepth(node.hierarchy_path)),
  );

  return nodes.filter(
    (node) => getPathDepth(node.hierarchy_path) === minPathLength,
  );
}

function flattenNode(node, minDepth, rows) {
  if (!node) return;

  const depth = getPathDepth(node.hierarchy_path);
  rows.push({
    name: node.machine_name || "Unknown",
    consumed: parseEnergyValue(node.energy_consumed),
    generated: parseEnergyValue(node.energy_generated),
    net: parseEnergyValue(node.energy),
    indent: Math.max(0, depth - minDepth),
    category: node.category || "",
  });

  (node.children || []).forEach((child) => flattenNode(child, minDepth, rows));
}

export function buildEnergyBreakdownRows(hierarchyData, scopeNodeName) {
  if (!hierarchyData || typeof hierarchyData !== "object") return [];

  const isWholeFacility = !scopeNodeName || scopeNodeName === "Usage";

  if (!isWholeFacility) {
    const scopedNode = hierarchyData[scopeNodeName];
    if (!scopedNode) return [];
    const rows = [];
    flattenNode(scopedNode, getPathDepth(scopedNode.hierarchy_path), rows);
    return rows;
  }

  const roots = getRootNodes(hierarchyData);
  if (!roots.length) return [];

  const minDepth = Math.min(...roots.map((root) => getPathDepth(root.hierarchy_path)));
  const rows = [];
  roots.forEach((root) => flattenNode(root, minDepth, rows));
  return rows;
}

export async function fetchEnergyHierarchy(username, dateRange) {
  if (!username || !dateRange?.start || !dateRange?.end) {
    throw new Error("Missing username or date range.");
  }

  const response = await api.get("/energy-hierarchy-tree", {
    params: {
      username,
      start: formatReportDateTime(dateRange.start),
      end: formatReportDateTime(dateRange.end),
    },
  });

  return response.data;
}

function findMachineEntry(machines, targetName) {
  if (!machines || !targetName) return null;
  return (
    Object.values(machines).find((machine) => machine?.name === targetName) ||
    null
  );
}

/**
 * Daily consumed (kWh) and generated (kWh_neg) for whole facility (Usage)
 * or a scoped machine/branch from GET /machines_data mode=day.
 */
export async function fetchDailyEnergySeries(username, dateRange, scopeNodeName) {
  if (!username || !dateRange?.start || !dateRange?.end) {
    throw new Error("Missing username or date range.");
  }

  const isWholeFacility = !scopeNodeName || scopeNodeName === "Usage";
  const selectedMachines = isWholeFacility ? "Usage" : scopeNodeName;

  const response = await api.get("/machines_data", {
    params: {
      username,
      mode: "day",
      start: formatReportDateTime(dateRange.start),
      end: formatReportDateTime(dateRange.end),
      selected_machines: selectedMachines,
    },
  });

  const raw = response.data?.data || response.data;
  const dates = raw?.["Date & Time"] || [];
  const machineEntry = findMachineEntry(
    raw?.machines,
    isWholeFacility ? "Usage" : scopeNodeName,
  );

  if (!dates.length || !machineEntry?.data) {
    return [];
  }

  // Check multiple possible key variations returned by backend machines_data
  const findSeries = (dataObj, matchers) => {
    if (!dataObj || typeof dataObj !== "object") return [];
    for (const matcher of matchers) {
      if (Array.isArray(dataObj[matcher])) return dataObj[matcher];
      const foundKey = Object.keys(dataObj).find(
        (k) => k.toLowerCase() === matcher.toLowerCase()
      );
      if (foundKey && Array.isArray(dataObj[foundKey])) return dataObj[foundKey];
    }
    return [];
  };

  const consumedSeries = findSeries(machineEntry.data, [
    "kWh",
    "power_kw",
    "energy",
    "kW",
    "usage",
  ]);
  const generatedSeries = findSeries(machineEntry.data, [
    "kWh_neg",
    "kw_neg",
    "energy_neg",
    "energy_negative",
    "generated",
  ]);

  return dates.map((date, index) => ({
    date,
    consumed: parseEnergyValue(consumedSeries[index]),
    generated: parseEnergyValue(generatedSeries[index]),
  }));
}

/**
 * Hourly consumed energy (kWh) for a scoped machine from GET /machines_data mode=hour.
 */
export async function fetchHourlyEnergySeries(username, dateRange, scopeNodeName) {
  if (!username || !dateRange?.start || !dateRange?.end) {
    throw new Error("Missing username or date range.");
  }

  const isWholeFacility = !scopeNodeName || scopeNodeName === "Usage";
  const selectedMachines = isWholeFacility ? "Usage" : scopeNodeName;

  const response = await api.get("/machines_data", {
    params: {
      username,
      mode: "hour",
      start: formatReportDateTime(dateRange.start),
      end: formatReportDateTime(dateRange.end),
      selected_machines: selectedMachines,
      metric: "energy",
    },
  });

  const raw = response.data?.data || response.data;
  const timestamps = raw?.["Date & Time"] || [];
  const machineEntry = findMachineEntry(
    raw?.machines,
    isWholeFacility ? "Usage" : scopeNodeName,
  );

  if (!timestamps.length || !machineEntry?.data) {
    return [];
  }

  const findSeries = (dataObj, matchers) => {
    if (!dataObj || typeof dataObj !== "object") return [];
    for (const matcher of matchers) {
      if (Array.isArray(dataObj[matcher])) return dataObj[matcher];
      const foundKey = Object.keys(dataObj).find(
        (k) => k.toLowerCase() === matcher.toLowerCase()
      );
      if (foundKey && Array.isArray(dataObj[foundKey])) return dataObj[foundKey];
    }
    return [];
  };

  const consumedSeries = findSeries(machineEntry.data, [
    "kWh",
    "power_kw",
    "energy",
    "kW",
    "usage",
  ]);

  return timestamps.map((timestamp, index) => ({
    timestamp,
    consumed: parseEnergyValue(consumedSeries[index]),
  }));
}
